/**
 * Payment Service - Manages payment records in Firebase
 */

import { 
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  updateDoc, 
  query, 
  where, 
  Timestamp 
} from 'firebase/firestore';
import { db } from './firebase';

export const PAYMENT_STATUS = {
  PENDING: 'pending',
  SUCCESS: 'success',
  FAILED: 'failed',
  REFUNDED: 'refunded',
};

export const PAYMENT_METHOD = {
  RAZORPAY: 'razorpay',
  CARD: 'card',
  UPI: 'upi',
  NETBANKING: 'netbanking',
  WALLET: 'wallet',
};

/**
 * Create payment record
 * @param {Object} paymentData - Payment details
 * @returns {Promise<Object>} Result with payment ID
 */
export const createPaymentRecord = async (paymentData) => {
  try {
    const paymentRecord = {
      userId: paymentData.userId,
      packageId: paymentData.packageId,
      packageName: paymentData.packageName,
      amount: paymentData.amount,
      currency: paymentData.currency || 'INR',
      gstAmount: paymentData.gstAmount || 0,
      totalAmount: paymentData.totalAmount,
      paymentMethod: paymentData.paymentMethod || PAYMENT_METHOD.RAZORPAY,
      status: PAYMENT_STATUS.PENDING,
      razorpayOrderId: paymentData.orderId || null,
      razorpayPaymentId: paymentData.paymentId || null,
      razorpaySignature: paymentData.signature || null,
      userEmail: paymentData.userEmail,
      userName: paymentData.userName,
      userPhone: paymentData.userPhone,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      metadata: paymentData.metadata || {},
    };

    const docRef = await addDoc(collection(db, 'payments'), paymentRecord);
    
    return { 
      success: true, 
      paymentId: docRef.id,
      paymentRecord 
    };
  } catch (error) {
    console.error('Error creating payment record:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update payment status
 * @param {string} paymentId - Firebase payment document ID
 * @param {Object} updateData - Data to update
 * @returns {Promise<Object>} Update result
 */
export const updatePaymentStatus = async (paymentId, updateData) => {
  try {
    const paymentRef = doc(db, 'payments', paymentId);
    
    await updateDoc(paymentRef, {
      ...updateData,
      updatedAt: Timestamp.now(),
    });

    return { success: true };
  } catch (error) {
    console.error('Error updating payment status:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Mark payment as successful
 * @param {string} paymentId - Firebase payment document ID
 * @param {Object} paymentDetails - Razorpay payment details
 * @returns {Promise<Object>} Update result
 */
export const markPaymentSuccess = async (paymentId, paymentDetails) => {
  try {
    const updateData = {
      status: PAYMENT_STATUS.SUCCESS,
      razorpayPaymentId: paymentDetails.paymentId,
      completedAt: Timestamp.now(),
    };
    
    // Only add orderId and signature if they exist (they won't in direct payment mode)
    if (paymentDetails.orderId) {
      updateData.razorpayOrderId = paymentDetails.orderId;
    }
    if (paymentDetails.signature) {
      updateData.razorpaySignature = paymentDetails.signature;
    }
    
    return await updatePaymentStatus(paymentId, updateData);
  } catch (error) {
    console.error('Error marking payment as successful:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Mark payment as failed
 * @param {string} paymentId - Firebase payment document ID
 * @param {string} errorMessage - Error message
 * @returns {Promise<Object>} Update result
 */
export const markPaymentFailed = async (paymentId, errorMessage) => {
  try {
    return await updatePaymentStatus(paymentId, {
      status: PAYMENT_STATUS.FAILED,
      errorMessage,
      failedAt: Timestamp.now(),
    });
  } catch (error) {
    console.error('Error marking payment as failed:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get user payments
 * @param {string} userId - User ID
 * @returns {Promise<Object>} User payments
 */
export const getUserPayments = async (userId) => {
  try {
    const q = query(
      collection(db, 'payments'),
      where('userId', '==', userId)
    );

    const querySnapshot = await getDocs(q);
    const payments = [];

    querySnapshot.forEach((doc) => {
      payments.push({
        id: doc.id,
        ...doc.data(),
      });
    });

    // Sort in memory by createdAt (newest first)
    payments.sort((a, b) => {
      const aTime = a.createdAt?.toMillis() || 0;
      const bTime = b.createdAt?.toMillis() || 0;
      return bTime - aTime;
    });

    return { success: true, payments };
  } catch (error) {
    console.error('Error fetching user payments:', error);
    return { success: false, error: error.message, payments: [] };
  }
};

/**
 * Get successful payments for a user
 * @param {string} userId - User ID
 * @returns {Promise<Object>} Successful payments
 */
export const getUserSuccessfulPayments = async (userId) => {
  try {
    const q = query(
      collection(db, 'payments'),
      where('userId', '==', userId),
      where('status', '==', PAYMENT_STATUS.SUCCESS)
    );

    const querySnapshot = await getDocs(q);
    const payments = [];

    querySnapshot.forEach((doc) => {
      payments.push({
        id: doc.id,
        ...doc.data(),
      });
    });

    // Sort in memory by createdAt (newest first)
    payments.sort((a, b) => {
      const aTime = a.createdAt?.toMillis() || 0;
      const bTime = b.createdAt?.toMillis() || 0;
      return bTime - aTime;
    });

    return { success: true, payments };
  } catch (error) {
    console.error('Error fetching successful payments:', error);
    return { success: false, error: error.message, payments: [] };
  }
};

/**
 * Get payment analytics
 * @returns {Promise<Object>} Payment analytics
 */
export const getPaymentAnalytics = async () => {
  try {
    const paymentsSnapshot = await getDocs(collection(db, 'payments'));
    
    const analytics = {
      totalPayments: paymentsSnapshot.size,
      successfulPayments: 0,
      failedPayments: 0,
      pendingPayments: 0,
      totalRevenue: 0,
      packageBreakdown: {},
      methodBreakdown: {},
    };

    paymentsSnapshot.forEach((doc) => {
      const payment = doc.data();
      
      // Count by status
      switch (payment.status) {
        case PAYMENT_STATUS.SUCCESS:
          analytics.successfulPayments++;
          analytics.totalRevenue += payment.totalAmount || payment.amount || 0;
          break;
        case PAYMENT_STATUS.FAILED:
          analytics.failedPayments++;
          break;
        case PAYMENT_STATUS.PENDING:
          analytics.pendingPayments++;
          break;
      }

      // Package breakdown
      if (payment.status === PAYMENT_STATUS.SUCCESS) {
        const packageName = payment.packageName || 'Unknown';
        if (!analytics.packageBreakdown[packageName]) {
          analytics.packageBreakdown[packageName] = {
            count: 0,
            revenue: 0,
          };
        }
        analytics.packageBreakdown[packageName].count++;
        analytics.packageBreakdown[packageName].revenue += payment.totalAmount || payment.amount || 0;
      }

      // Payment method breakdown
      const method = payment.paymentMethod || 'Unknown';
      analytics.methodBreakdown[method] = (analytics.methodBreakdown[method] || 0) + 1;
    });

    return { success: true, analytics };
  } catch (error) {
    console.error('Error fetching payment analytics:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get payment totals by user type
 * @returns {Promise<Object>} Payment totals by user type
 */
export const getPaymentTotalsByUserType = async () => {
  try {
    const q = query(
      collection(db, 'payments'),
      where('status', '==', PAYMENT_STATUS.SUCCESS)
    );
    
    const querySnapshot = await getDocs(q);
    
    const totals = {
      seeker: {
        count: 0,
        amount: 0,
      },
      employer: {
        count: 0,
        amount: 0,
      },
      total: {
        count: 0,
        amount: 0,
      }
    };
    
    querySnapshot.forEach((doc) => {
      const payment = doc.data();
      const userType = payment.metadata?.userType || 'seeker';
      const amount = payment.totalAmount || payment.amount || 0;
      
      totals[userType].count++;
      totals[userType].amount += amount;
      
      totals.total.count++;
      totals.total.amount += amount;
    });
    
    return { success: true, totals };
  } catch (error) {
    console.error('Error getting payment totals by user type:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Process refund
 * @param {string} paymentId - Firebase payment document ID
 * @param {Object} refundData - Refund details
 * @returns {Promise<Object>} Refund result
 */
export const processPaymentRefund = async (paymentId, refundData) => {
  try {
    return await updatePaymentStatus(paymentId, {
      status: PAYMENT_STATUS.REFUNDED,
      refundAmount: refundData.amount,
      refundReason: refundData.reason,
      refundedAt: Timestamp.now(),
      refundId: refundData.refundId,
    });
  } catch (error) {
    console.error('Error processing refund:', error);
    return { success: false, error: error.message };
  }
};

export default {
  PAYMENT_STATUS,
  PAYMENT_METHOD,
  createPaymentRecord,
  updatePaymentStatus,
  markPaymentSuccess,
  markPaymentFailed,
  getUserPayments,
  getUserSuccessfulPayments,
  getPaymentAnalytics,
  getPaymentTotalsByUserType,
  processPaymentRefund,
};
